import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupportAuth } from '../contexts/SupportAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, KeyRound } from 'lucide-react';
import { supportAuthAPI } from '../lib/api';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../lib/analytics';
import { cabinetPath } from '../lib/domains';

interface SupportAuthPageProps {
    mode: 'login' | 'register';
}

export const SupportAuthPage: React.FC<SupportAuthPageProps> = ({ mode }) => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '', // Только для регистрации
    });
    const [error, setError] = useState<string | null>(null);
    const [resetMessage, setResetMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isResetLoading, setIsResetLoading] = useState(false);

    const { login } = useSupportAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Получаем адрес для редиректа (если есть)
    const from = location.state?.from?.pathname || cabinetPath('support', '/dashboard');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        if (error) setError(null);
        if (resetMessage) setResetMessage(null);
    };

    const handlePasswordResetRequest = async () => {
        const identifier = formData.username.trim();
        setError(null);
        setResetMessage(null);

        if (!identifier) {
            setError('Введите имя пользователя или email');
            return;
        }

        setIsResetLoading(true);
        try {
            const response = await supportAuthAPI.requestPasswordReset(identifier);
            setResetMessage(response.message);
        } catch (err: any) {
            if (err.response?.data?.detail) {
                setError(err.response.data.detail);
            } else {
                setError('Не удалось отправить запрос');
            }
        } finally {
            setIsResetLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            if (mode === 'login') {
                await login({
                    username: formData.username,
                    password: formData.password,
                });
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SUCCEEDED, {
                    user_type: 'support',
                    auth_mode: 'login',
                });
                navigate(from, { replace: true });
            } else {
                // Регистрация пока не реализована публично, или использует тот же API
                // Но обычно саппортов создают админы. 
                // Для теста можно использовать register эндпоинт если он открыт
                setError("Регистрация саппорта через UI пока закрыта. Используйте созданных пользователей.");
            }
        } catch (err: any) {
            if (err.response?.data?.detail) {
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_FAILED, {
                    user_type: 'support',
                    auth_mode: mode,
                    error_message: err.response.data.detail,
                });
                setError(err.response.data.detail);
            } else {
                const message = 'Произошла ошибка. Попробуйте позже.';
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_FAILED, {
                    user_type: 'support',
                    auth_mode: mode,
                    error_message: message,
                });
                setError(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
            <Card className="w-full max-w-md shadow-lg">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                        {mode === 'login' ? 'Вход для поддержки' : 'Регистрация поддержки'}
                    </CardTitle>
                    <CardDescription>
                        {mode === 'login'
                            ? 'Войдите в систему администрирования'
                            : 'Создайте новый аккаунт поддержки'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md flex items-center gap-2 text-red-600 dark:text-red-300 text-sm">
                            <AlertCircle className="h-4 w-4" />
                            <span>{error}</span>
                        </div>
                    )}
                    {resetMessage && (
                        <div className="mb-4 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md text-green-700 dark:text-green-300 text-sm">
                            {resetMessage}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username">Имя пользователя</Label>
                            <Input
                                id="username"
                                name="username"
                                type="text"
                                placeholder="support_user"
                                required
                                value={formData.username}
                                onChange={handleChange}
                            />
                        </div>

                        {mode === 'register' && (
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="support@example.com"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="password">Пароль</Label>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                placeholder="••••••••"
                                required
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                            disabled={isLoading || isResetLoading}
                        >
                            {isLoading ? 'Загрузка...' : (mode === 'login' ? 'Войти' : 'Зарегистрироваться')}
                        </Button>
                        {mode === 'login' && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="w-full text-slate-700 dark:text-slate-200"
                                onClick={handlePasswordResetRequest}
                                disabled={isLoading || isResetLoading}
                            >
                                <KeyRound className="h-4 w-4 mr-2" />
                                {isResetLoading ? 'Отправка...' : 'Забыли пароль?'}
                            </Button>
                        )}
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};
